import { useEffect, useState } from "react";
import "../Food/RecipeList.css";
import "./BabyTracker.css";
import {
  deleteFrozenMilkEntry,
  fetchFrozenMilkStorage,
  getNowLocalDateTime,
  saveFrozenMilkEntry,
} from "./BabyTrackerShared";
import BabyTrackerLoading from "./BabyTrackerLoading";

const formatOunces = (value) => {
  const numericValue = Number(value || 0);
  if (Number.isNaN(numericValue)) return "0";
  return numericValue.toFixed(1).replace(/\.0$/, "");
};

const defaultFormState = (entryType = "add") => ({
  entryType,
  amountOunces: "",
  occurredAt: getNowLocalDateTime(),
  notes: "",
});

const entryLabels = {
  add: {
    action: "Add milk",
    heading: "Add frozen milk",
    past: "Added",
    submit: "Save milk",
  },
  remove: {
    action: "Remove milk",
    heading: "Remove frozen milk",
    past: "Removed",
    submit: "Save removal",
  },
};

const BabyTrackerFrozenMilk = () => {
  const [storage, setStorage] = useState({ totalOunces: 0, entries: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultFormState());

  const loadStorage = () => {
    setLoading(true);
    setLoadError("");
    fetchFrozenMilkStorage()
      .then((data) => {
        setStorage(data);
        setLoading(false);
      })
      .catch(() => {
        setLoadError("Unable to load frozen milk storage.");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadStorage();
  }, []);

  const startEntry = (entryType) => {
    setForm(defaultFormState(entryType));
    setShowForm(true);
    setLoadError("");
  };

  const updateForm = (field, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  };

  const cancelForm = () => {
    setShowForm(false);
    setForm(defaultFormState());
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSaving(true);
    setLoadError("");
    saveFrozenMilkEntry(form)
      .then(() => {
        setSaving(false);
        setShowForm(false);
        setForm(defaultFormState());
        loadStorage();
      })
      .catch(() => {
        setSaving(false);
        setLoadError("Unable to save frozen milk entry.");
      });
  };

  const handleDelete = (entry) => {
    const label = entryLabels[entry.entryType]?.past || "Entry";
    const confirmed = window.confirm(`Delete this frozen milk entry?\n\n${label} ${formatOunces(entry.amountOunces)} oz`);
    if (!confirmed) return;

    deleteFrozenMilkEntry(entry.id)
      .then(loadStorage)
      .catch(() => setLoadError("Unable to delete frozen milk entry."));
  };

  const activeLabels = entryLabels[form.entryType] || entryLabels.add;

  return (
    <div className="BabyTrackerPage">
      <div className="searchBarDiv formViewWrapper BabyTrackerIntro">
        <div>
          <h2 className="BabyTrackerTitle">Frozen milk</h2>
          <p className="BabyTrackerSubtitle">
            Track milk added to and removed from freezer storage.
          </p>
        </div>
      </div>

      <div className="shoppingListDiv BabyTrackerPanel BabyTrackerSinglePanel">
        <div className="BabyTrackerStorageTotal">
          <span>Current storage</span>
          <strong>{formatOunces(storage.totalOunces)} oz</strong>
        </div>

        {!showForm ? (
          <div className="BabyTrackerActionRow">
            <button className="summaryButton" type="button" onClick={() => startEntry("add")}>
              Add milk
            </button>
            <button className="summaryButton" type="button" onClick={() => startEntry("remove")}>
              Remove milk
            </button>
          </div>
        ) : (
          <form className="BabyTrackerForm" onSubmit={handleSubmit}>
            <h3>{activeLabels.heading}</h3>
            <div className="inputDiv">
              <label>Amount</label>
              <input
                className="inputBox addInput"
                min="0.1"
                step="0.1"
                type="number"
                value={form.amountOunces}
                onChange={(event) => updateForm("amountOunces", event.target.value)}
                required
              />
              <span className="BabyTrackerInputSuffix">oz</span>
            </div>
            <div className="inputDiv">
              <label>Time</label>
              <input
                className="inputBox addInput"
                type="datetime-local"
                value={form.occurredAt}
                onChange={(event) => updateForm("occurredAt", event.target.value)}
                required
              />
            </div>
            <div className="inputDiv">
              <label>Notes</label>
              <textarea
                className="inputBox addInput"
                rows="3"
                value={form.notes}
                onChange={(event) => updateForm("notes", event.target.value)}
              />
            </div>
            <div className="BabyTrackerActionRow">
              <button className="summaryButton" type="submit" disabled={saving}>
                {saving ? "Saving..." : activeLabels.submit}
              </button>
              <button className="summaryButton" type="button" onClick={cancelForm}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {loadError ? <p className="BabyTrackerError">{loadError}</p> : null}
      </div>

      <div className="shoppingListDiv BabyTrackerPanel BabyTrackerSinglePanel">
        <h3>Recent storage changes</h3>
        {loading ? <BabyTrackerLoading label="Loading frozen milk" /> : null}
        {!loading && storage.entries?.length ? (
          <div className="BabyTrackerHistoryList">
            {storage.entries.map((entry) => {
              const labels = entryLabels[entry.entryType] || entryLabels.add;
              return (
                <div className="BabyTrackerHistoryCard BabyTrackerFrozenMilkEntry" key={entry.id}>
                  <div>
                    <strong>{labels.past} {formatOunces(entry.amountOunces)} oz</strong>
                    <div>{entry.occurredAt}</div>
                    {entry.notes ? <p>{entry.notes}</p> : null}
                  </div>
                  <button className="summaryButton" type="button" onClick={() => handleDelete(entry)}>
                    Delete
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}
        {!loading && !storage.entries?.length ? <p>No frozen milk storage changes yet.</p> : null}
      </div>
    </div>
  );
};

export default BabyTrackerFrozenMilk;
